import React from 'react';
import { TableColumn } from 'react-data-table-component';
import { Button } from '@mui/material';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import moment from 'moment';
import { ShowAlert } from '../../ShowAlert';
import dayjs from "dayjs";
import Tooltip from '@mui/material/Tooltip';

type Primitive = string | number | boolean;
type Selector<T> = (row: T, rowIndex?: number) => Primitive;

export interface DownloadCsvProps<D> {
    columns: TableColumn<D>[];
    data: D[];
    csvFileName?: string;
    selectedDate: { startDate: Date | null, endDate: Date | null };
    isRestrictExportFor31Days?: boolean;
    prepareCSVCustom?: (objDownloadCSV: DownloadCsv<D>) => void;
    parentComponent?: React.Component<any, any>;
}

export interface DownloadCsvState {
}


class DownloadCsv<D> extends React.Component<DownloadCsvProps<D>, DownloadCsvState> {

    constructor(props_: DownloadCsvProps<D>) {
        super(props_);

        this.state = {}
    }

    public render() {
        return (
            <Tooltip title="Download CSV" arrow>
                {/* <Button onClick={e => {
                    this.downloadCSV(this.props.data);
                }}>Export</Button> */}
                <Button color="primary"
                    data-testid="download-csv-button"
                    onClick={(ev_) => {
                        this.onClickDownloadCSV(this.props.data);
                    }}
                >
                    <DownloadForOfflineIcon fontSize="large" color='secondary' />
                </Button>
            </Tooltip>
        )
    }

    private onClickDownloadCSV(array: D[]) {
        if (this.props.isRestrictExportFor31Days) {
            let startDate = moment(this.props.selectedDate.startDate);
            let endDate = moment(this.props.selectedDate.endDate);
            let dateDiff = (endDate).diff(startDate, 'days'); // need to handle code for null date values during on search
            if (dateDiff <= 31 && this.props.data.length >= 1) {
                this.prepareCSVConditional();
            } else if ((dateDiff <= 31) && (this.props.data === undefined || this.props.data.length < 1)) {
                this.errormsg();
            } else if (dateDiff > 31) {
                this.errormsg("The number of days should not exceed 31.");
            }
        } else {//For other reports
            if (array.length >= 1) {
                this.prepareCSVConditional();
            } else {
                this.errormsg();
            }
        }
    }

    public errormsg(message = "No data to download") {
        ShowAlert("Error", message, "error")
    }

    public formatToLocalTime(utcString: string): string {
        if (!utcString) return ''; // Handle empty or invalid values

        return dayjs(utcString).format("DD-MM-YYYY HH:mm");
    }

    public toCsvByReplacingSingleToDoubleQuote(arr: any[][]) {
        return arr.map(row =>
            row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
    }

    private toCsv(arr: D[][]) {
        return arr.map(row =>
            row.map((val: any) => isNaN(val) || val === '' ? JSON.stringify(val) : +val).join(',')
        ).join('\n');
    }

    public prepareCSVConditional() {
        if (this.props.prepareCSVCustom) {
            this.props.prepareCSVCustom(this);
        }
        else {
            this.prepareCSV();
        }
    }

    public prepareCSV(fileNameStatic: string | null = null) {
        let csv = this.toCsv(this.getPivotData(this.props.data, this.props.columns));
        const fileName = `${this.props.csvFileName && this.props.csvFileName !== '' ? this.props.csvFileName : (fileNameStatic ? fileNameStatic : 'export')}.csv`;
        this.downloadCSV(csv, fileName);
    }

    public downloadCSV(csv: string, fileName: string): void {
        const link = document.createElement('a');
        if (csv == null) return;
        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }
        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', fileName);
        link.click();
    }

    private getPivotData(data: D[], columns: TableColumn<D>[]) {
        let header: string[] = [];
        let arrID: string[] = [];
        let objIsMobileField: Record<string, boolean | undefined> = {};
        let objSelector: Record<string, (Selector<D> | undefined)> = {};
        columns.forEach(column => {
            if (column.hide !== 1 && column.id && !column.omit) {
                let columnId = (column.id as string);
                arrID.push(columnId);
                header.push((column.name || "").toString());
                objSelector[columnId] = column.selector;
                objIsMobileField[columnId] = column.compact;//compact is used to identify whther it is mobile field or not
            }
        })
        const rows = data.map(obj =>
            arrID.map(id => {
                let value = (obj as any)[id] ?? ''; // Default to empty string if value is missing
                //For formatting the values
                if (objSelector[id]) {
                    value = (objSelector[id] as Selector<D>)(obj);
                }
                if (objIsMobileField[id]) {
                    value = value.replace(/-/g, ' ');
                }
                return value;
            })
        );
        return [header, ...rows];
    }
}

export default DownloadCsv;